import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/movies';
import { siteInfo } from '@/mocks/siteInfo';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isActive = (slug: string) => {
    if (slug === 'all' && location.pathname === '/') return true;
    return location.pathname === `/category/${slug}`;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${isScrolled ? 'bg-primary-500' : 'bg-primary-500'} transition-colors`}>
              <i className="ri-movie-2-line text-xl text-background-50"></i>
            </div>
            <div className="flex flex-col">
              <span className={`text-lg font-bold font-heading leading-tight ${isScrolled ? 'text-foreground-950' : 'text-background-50'}`}>
                {siteInfo.name}
              </span>
              <span className={`text-[10px] leading-tight ${isScrolled ? 'text-foreground-500' : 'text-background-50/80'}`}>
                4K超清影视
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap ${
                  isActive(cat.slug)
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : isScrolled
                      ? 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/15'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  isScrolled
                    ? 'text-foreground-600 hover:bg-background-100'
                    : 'text-background-50/90 hover:bg-background-50/15'
                }`}
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-background-50 rounded-lg p-2 shadow-lg border border-background-200">
                  <div className="flex items-center gap-2 bg-background-100 rounded-md px-3 py-2">
                    <i className="ri-search-line text-foreground-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="搜索电影、电视剧..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent border-none outline-none text-sm text-foreground-900 w-full placeholder:text-foreground-400"
                    />
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/about"
              className={`text-sm transition-colors whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-600 hover:text-primary-500'
                  : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-background-50 hover:bg-background-50/15'
            }`}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-4 px-2">
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap ${
                    isActive(cat.slug)
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-background-100 rounded-md px-3 py-2 mb-3">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索电影、电视剧..."
                className="bg-transparent border-none outline-none text-sm text-foreground-900 w-full placeholder:text-foreground-400"
              />
            </div>
            <Link
              to="/about"
              className="block px-3 py-2 text-sm text-foreground-600 hover:text-primary-500 rounded-md hover:bg-background-100 whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}